import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CP, CapCard, GaugeDonut, UserSearch, InitialAvatar, Pill, HBar } from "./ui";
import { Certificate, RocketLaunch, HandsClapping, Exam, CheckCircle, Briefcase } from "@phosphor-icons/react";

const TYPE_ICONS = {
  certification: [Certificate, "#7C5CFC", "#F3F0FF"],
  project: [RocketLaunch, "#2563EB", "#EFF4FF"],
  endorsement: [HandsClapping, "#F59E0B", "#FFF7E6"],
  assessment: [Exam, "#10B981", "#ECFDF5"],
  task: [Briefcase, "#0EA5E9", "#EFF9FF"],
};

export default function EvidencePanel() {
  const [target, setTarget] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (userId, skill) => {
    setBusy(true);
    api.get(`/capability/evidence/${userId}`, { params: skill ? { skill } : {} })
      .then(({ data }) => { setData(data); setTarget({ user_id: userId }); })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => data?.user_id && load(data.user_id)).catch(() => {});
  }, []);

  const overallColor = data?.overall >= 4 ? CP.green : data?.overall >= 3 ? CP.amber : CP.red;

  return (
    <div className="space-y-4" data-testid="evidence-panel">
      <div className="max-w-md"><UserSearch onSelect={(u) => load(u.user_id)} placeholder="Search an employee…" testid="evidence-user-search" /></div>

      {busy && <div className="text-sm py-8 text-center" style={{ color: CP.sub }}>Computing confidence…</div>}

      {data && !busy && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <InitialAvatar name={data.user?.name} size={36} />
              <div>
                <div className="text-sm font-bold" style={{ color: CP.text }}>{data.user?.name}</div>
                <div className="text-[11px]" style={{ color: CP.sub }}>{data.user?.title || data.user?.role}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 ml-auto">
              {(data.skills || []).slice(0, 8).map((s) => (
                <button key={s} onClick={() => load(target.user_id, s)} data-testid={`evidence-skill-${s}`}
                  className="text-[11px] px-2.5 py-1 rounded-full capitalize font-medium transition-colors"
                  style={data.skill === s
                    ? { background: CP.navy, color: "#fff" }
                    : { background: "#fff", color: CP.sub, border: `1px solid ${CP.border}` }}>{s}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <CapCard title="Evidence & Confidence" subtitle={<span className="capitalize">{data.skill}</span>} testid="evidence-gauge">
              <div className="flex flex-col items-center pt-1">
                <GaugeDonut value={data.overall} max={5} label={data.label} color={overallColor} size={160} />
              </div>
              <div className="mt-3 space-y-2">
                <div className="text-xs font-semibold" style={{ color: CP.sub }}>Score Breakdown</div>
                {(data.breakdown || []).map((b) => (
                  <HBar key={b.factor} label={b.factor} value={b.score} max={5} color={b.score >= 4 ? CP.green : b.score >= 3 ? CP.blue : CP.amber} />
                ))}
              </div>
            </CapCard>

            <div className="lg:col-span-2 space-y-4">
              <CapCard title="Evidence" subtitle={`${(data.evidence || []).length} evidence points`} testid="evidence-list">
                <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                  {(data.evidence || []).map((e, i) => {
                    const [Icon, fg, bg] = TYPE_ICONS[e.type] || TYPE_ICONS.task;
                    return (
                      <div key={i} className="flex items-start gap-3 rounded-lg p-3" style={{ border: `1px solid ${CP.border}` }} data-testid={`evidence-item-${i}`}>
                        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: bg }}>
                          <Icon size={18} color={fg} weight="duotone" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-1.5" style={{ color: CP.text }}>
                            {e.title}
                            {e.verified && <CheckCircle size={14} color={CP.green} weight="fill" />}
                          </div>
                          <div className="text-[11px]" style={{ color: CP.sub }}>{e.meta} · {(e.date || "").slice(0, 10)}</div>
                        </div>
                        <Pill tone={e.score >= 4.5 ? "green" : e.score >= 3.5 ? "blue" : "amber"}>{e.score}/5</Pill>
                      </div>
                    );
                  })}
                  {(data.evidence || []).length === 0 && <div className="text-xs py-6 text-center" style={{ color: CP.sub }}>No evidence collected yet for this skill.</div>}
                </div>
              </CapCard>

              <CapCard title="Why this score?" testid="evidence-explanation">
                <p className="text-sm leading-relaxed" style={{ color: CP.text }}>{data.explanation}</p>
              </CapCard>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
